// Workspace App — Admin approval queue.
// Shows all pending sign-ups with Approve/Reject actions.
// Reuses the MarkovLab admin auth guard.

import React from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function WorkspaceAdminPendingPage() {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/workspace/admin/pending");
      setRows(data.pending || []);
    } catch (e) {
      toast.error("Failed to load pending queue");
    } finally { setLoading(false); }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const approve = async (uid) => {
    try {
      await api.post(`/workspace/admin/approve/${uid}`);
      toast.success("User approved");
      load();
    } catch { toast.error("Approval failed"); }
  };
  const reject = async (uid) => {
    if (!window.confirm("Reject this user?")) return;
    try {
      await api.post(`/workspace/admin/reject/${uid}`, { send_email: false });
      toast.success("User rejected");
      load();
    } catch { toast.error("Rejection failed"); }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="workspace-admin-pending">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-lg font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>
          Pending Approvals
          {rows.length > 0 && (
            <span className="ml-2 text-sm font-mono-data text-amber-500" data-testid="pending-count">
              ({rows.length})
            </span>
          )}
        </h1>
        <Button size="sm" variant="outline" onClick={load} disabled={loading}
          data-testid="workspace-pending-refresh">
          <RefreshCw className={`w-3 h-3 mr-1 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {rows.length === 0 ? (
        <div className="text-sm text-center py-12" style={{ color: "var(--hoip-text-tertiary)" }}>
          No users awaiting approval.
        </div>
      ) : (
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b" style={{ borderColor: "var(--hoip-border)" }}>
              <th className="text-left p-2 font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Email</th>
              <th className="text-left p-2 font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Name</th>
              <th className="text-left p-2 font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Requested</th>
              <th className="text-right p-2 font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(u => (
              <tr key={u.id} className="border-b" style={{ borderColor: "var(--hoip-divider)" }}
                data-testid={`pending-row-${u.email}`}>
                <td className="p-2 font-mono-data">{u.email}</td>
                <td className="p-2">{u.name || "—"}</td>
                <td className="p-2 font-mono-data text-[10px]" style={{ color: "var(--hoip-text-secondary)" }}>
                  {u.created_at?.slice(0, 16).replace("T", " ")}
                </td>
                <td className="p-2 text-right">
                  <Button size="sm" className="mr-1 h-6 text-[10px]" onClick={() => approve(u.id)}
                    data-testid={`approve-btn-${u.email}`}>
                    <CheckCircle2 className="w-3 h-3 mr-1" />Approve
                  </Button>
                  <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => reject(u.id)}
                    data-testid={`reject-btn-${u.email}`}>
                    <XCircle className="w-3 h-3 mr-1" />Reject
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
